import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Crown, ShieldCheck, User } from "@phosphor-icons/react";

const RoleIcon = ({ role }) => {
  if (role === "owner") return <Crown size={14} weight="fill" className="text-yellow-400" />;
  if (role === "admin") return <ShieldCheck size={14} weight="fill" className="text-white" />;
  return <User size={14} className="text-white/60" />;
};

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });

  const load = useCallback(async () => {
    const { data } = await api.get("/team/members");
    setMembers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team/invite", form);
      toast.success("Member added");
      setForm({ name: "", email: "", password: "", role: "member" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const canInvite = user?.role === "owner" || user?.role === "admin";

  return (
    <Layout>
      <div className="border-b border-white/10 px-10 py-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// members</div>
          <h1 className="font-heading text-5xl tracking-tight mt-2">Team</h1>
        </div>
        {canInvite && (
          <button data-testid="team-invite-button" onClick={() => setShowForm(true)}
            className="bg-white text-black px-4 py-2.5 rounded-sm flex items-center gap-2 hover:bg-white/90 transition-colors">
            <Plus size={16} weight="bold" /> Invite member
          </button>
        )}
      </div>

      <div className="p-10">
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-white/10 text-[11px] uppercase tracking-widest font-mono text-white/40">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 flex items-center gap-3">
                    <div className="h-8 w-8 rounded-sm bg-white/10 flex items-center justify-center font-mono text-xs">
                      {m.name[0]?.toUpperCase()}
                    </div>
                    {m.name}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-white/60">{m.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <RoleIcon role={m.role} />
                      <span className="text-xs uppercase tracking-wider font-mono">{m.role}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-white/50">
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// invite</div>
                <div className="font-heading text-2xl tracking-tight">Invite member</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-white/10 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={invite} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Name</label>
                <input data-testid="invite-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Email</label>
                <input data-testid="invite-form-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Temporary password</label>
                <input data-testid="invite-form-password" type="text" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6}
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors font-mono" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Role</label>
                <div className="mt-1 flex gap-2">
                  {["admin", "member"].map((r) => (
                    <button key={r} type="button" data-testid={`invite-role-${r}`} onClick={() => setForm({...form, role: r})}
                      className={`flex-1 px-3 py-2.5 border rounded-sm text-sm uppercase tracking-wider font-mono transition-colors ${
                        form.role === r ? "border-white bg-white text-black" : "border-white/10 text-white/60 hover:border-white/30"
                      }`}>{r}</button>
                  ))}
                </div>
              </div>
              <button data-testid="invite-form-submit" type="submit"
                className="w-full bg-white text-black py-2.5 rounded-sm font-medium hover:bg-white/90 transition-colors">
                Add member
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
